// src/components/chat/uiSignals.js — agent→UI side-channel (issue #6).
//
// The tutor agent's `ui_signal` tool emits one `custom` SSE frame per
// call with shape `{ ui_signal: { kind, payload } }`. The runtime's
// `case 'custom'` branch hands `evt.data` to `dispatchUiSignal`, which
// routes to a per-kind handler in the registry built by
// `makeUiSignalHandlers`.
//
// Pure pass-through: nothing here persists or yields a chat message —
// these are transient client-side effects only. Today only `toast` is
// fully wired (through `useToast()`); the other four kinds are
// intentional no-op stubs (full handlers are follow-up tickets). The
// registry SHAPE and dispatch are real so wiring a stub up later is a
// one-line change.

// Build the handler registry. `toast` is the object returned by
// `useToast()` ({ success, error, warning, info }). It may be null in
// non-provider contexts (e.g. some tests) — handlers guard for that.
export function makeUiSignalHandlers(toast) {
  return {
    toast: (payload) => {
      // Select toast.success / .error / .warning / .info by variant;
      // fall back to .info for an unrecognized variant.
      const variant = payload?.variant;
      const fn = (toast && toast[variant]) || toast?.info;
      if (typeof fn === 'function') fn(payload?.message ?? '');
    },
    // --- stubs (follow-up tickets) — registry shape is real ----------
    scroll: () => {},
    highlight: () => {},
    refresh_view: () => {},
    animate_progress: () => {},
  };
}

// Route one `custom` frame's data to its handler. NEVER throws: a
// malformed frame, an unknown kind, or a handler bug is swallowed with
// a single console.warn — a throw here would abort the whole turn
// render (the `custom` branch lives inside the stream loop).
export function dispatchUiSignal(handlers, data) {
  const signal = data?.ui_signal;
  if (!signal || typeof signal.kind !== 'string') {
    console.warn('[tutor] dropped malformed ui_signal custom frame', data);
    return;
  }
  const handler = handlers?.[signal.kind];
  if (typeof handler !== 'function') {
    console.warn(`[tutor] dropped ui_signal: unknown kind '${signal.kind}'`);
    return;
  }
  try {
    handler(signal.payload || {});
  } catch (err) {
    console.warn(`[tutor] ui_signal handler for '${signal.kind}' threw`, err);
  }
}
